import clsx from "clsx";
import { useEffect, useState } from "react";

import { isRtl } from "~/helpers/helpers.ts";

import { Icon } from "../../../assets/icons/Icon";
import { useElementWidth } from "../../../hooks/useElementWidth";
import MiddleTruncateTextPx from "../../dashboard/PageTable/MiddleTruncateUrl";
import FormLabel from "../../login/formLabel/FormLabel";
import Button from "../../mantine/button/Button";
import Switch from "../../switch/Switch";
import Modal from "../../ui/modal/Modal";
import Spinner from "../../ui/spinner/Spinner";
import TextArea from "../../ui/textArea/TextArea";
import styles from "../ContentItem.module.scss";
import { CategoryType } from "./OnPage";
import { ApprovePayload, PageItem, PageRecommendation } from "./types";

interface EditedSuggestion {
  final: string;
  active: boolean;
  lang: string | null;
}

interface ContentSchemaModalProps {
  editedSuggestion: EditedSuggestion;
  setEditedSuggestion: React.Dispatch<React.SetStateAction<EditedSuggestion>>;
}

interface CategoryPageItemProps {
  isDefaultOpen: boolean;
  item: PageItem;
  approve: (data: ApprovePayload) => void;
  isLoading: boolean;
  isDisabled: boolean;
  isWidgetActionLoading: boolean;
  hasPendingStatus?: boolean;
  currentCategory: CategoryType | undefined;
  editPageSuggestion: (data: { id: number; final: string }) => void;
  fetchDetails?: (pageId: number) => Promise<void>;
}

const ContentSchemaModal = ({
  editedSuggestion,
  setEditedSuggestion,
}: ContentSchemaModalProps) => {
  return (
    <div className={styles.editSuggestionContent}>
      <form>
        <FormLabel label="Suggestion" />
        <div
          className={clsx({ ["rtl"]: isRtl(editedSuggestion.lang) })}
          lang={editedSuggestion.lang || undefined}
        >
          <TextArea
            minRows={6}
            value={editedSuggestion.final}
            onChange={(e) =>
              setEditedSuggestion({
                ...editedSuggestion,
                final: e.target.value,
              })
            }
            placeholder="Edit Suggestion"
            style={{ width: "100%" }}
          />
        </div>
        <div className={styles.switchContainer}>
          <FormLabel label="Approve Changes " />
          <Switch
            checked={editedSuggestion.active}
            onChange={() => {
              const newActiveValue = !editedSuggestion.active;
              setEditedSuggestion((prev) => ({
                ...prev,
                active: newActiveValue,
              }));
            }}
          />
        </div>
      </form>
    </div>
  );
};

const renderContent = (
  detail: PageRecommendation,
  isCurrentItemLoading: boolean,
  handleApprove: (detail: PageRecommendation, event: React.MouseEvent) => void,
  categorySlug: string | undefined,
  editSuggestion: (detail: PageRecommendation, event: React.MouseEvent) => void,
  isDisabled: boolean,
) => {
  switch (categorySlug) {
    case "canonical-links":
    case "no-social-preview":
      return (
        <div key={detail.id} className={styles.currentItemContent}>
          <div className={styles.currentItemContentHeader}>
            <div className={styles.currentItemContentTitle}>
              {detail?.description && detail?.description}
            </div>
            <div className={styles.currentItemContentHeaderIcons}>
              <div className={styles.currentItemContentHeaderIcon}>
                {isCurrentItemLoading ? (
                  <Spinner size="1rem" />
                ) : (
                  <Button
                    disabled={isDisabled}
                    variant="nohoverWithPadding"
                    onClick={(event) => handleApprove(detail, event)}
                  >
                    <Icon
                      iconName={
                        detail?.active
                          ? "GreenCheckmarkIcon"
                          : "CloseIconCircled"
                      }
                      width="1rem"
                      height="1rem"
                    />
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className={styles.currentItemContentFlex}>
            <div>
              <div className={styles.currentItemContentTitle}>Existing</div>
              <div
                className={clsx(styles.currentItemContentSuggestion, {
                  ["rtl"]:
                    isRtl(detail?.lang) && (detail?.original || detail?.tag),
                })}
                lang={detail?.lang || undefined}
              >
                {detail?.original || detail?.tag || "Blank"}
              </div>
            </div>
            <div>
              <div className={styles.currentItemContentTitle}>Suggestion</div>
              <div
                className={clsx(styles.currentItemContentSuggestion, {
                  ["rtl"]: isRtl(detail?.lang) && detail?.final,
                })}
                lang={detail?.lang || undefined}
              >
                {detail?.final || "Blank"}
              </div>
            </div>
          </div>
        </div>
      );

    case "missing-meta-desc":
      return (
        <div>
          <div className={styles.currentItemContentHeaderIcons}>
            <Button
              disabled={isDisabled}
              variant="nohoverWithPadding"
              onClick={(event) => editSuggestion(detail, event)}
            >
              <Icon iconName="EditIcon" width="1rem" height="1rem" />
            </Button>
            <div className={styles.currentItemContentHeaderIcon}>
              {isCurrentItemLoading ? (
                <Spinner size="1rem" />
              ) : (
                <Button
                  disabled={isDisabled}
                  variant="nohoverWithPadding"
                  onClick={(event) => handleApprove(detail, event)}
                >
                  <Icon
                    iconName={
                      detail?.active ? "GreenCheckmarkIcon" : "CloseIconCircled"
                    }
                    width="1rem"
                    height="1rem"
                  />
                </Button>
              )}
            </div>
          </div>
          <div className={styles.currentItemContentFlex}>
            <div>
              <div className={styles.currentItemContentTitle}>Existing</div>
              <div
                className={clsx(styles.currentItemContentSuggestion, {
                  ["rtl"]:
                    isRtl(detail?.lang) && (detail?.original || detail?.tag),
                })}
                lang={detail?.lang || undefined}
              >
                {detail?.original || detail?.tag || "Blank"}
              </div>
            </div>
            <div>
              <div className={styles.currentItemContentTitle}>Suggestion</div>
              <div
                className={clsx(styles.currentItemContentSuggestion, {
                  ["rtl"]: isRtl(detail?.lang) && detail?.final,
                })}
                lang={detail?.lang || undefined}
              >
                {detail?.final || "Blank"}
              </div>
            </div>
          </div>
        </div>
      );

    case "no-h1":
      return (
        <div key={detail.id} className={styles.currentItemContent}>
          <div className={styles.currentItemContentHeader}>
            <div className={styles.currentItemContentTitle}>
              {detail?.description && detail?.description}
            </div>
            <div className={styles.currentItemContentHeaderIcons}>
              <div className={styles.currentItemContentHeaderIcon}>
                {isCurrentItemLoading ? (
                  <Spinner size="1rem" />
                ) : (
                  <Button
                    disabled={isDisabled}
                    variant="nohoverWithPadding"
                    onClick={(event) => handleApprove(detail, event)}
                  >
                    <Icon
                      iconName={
                        detail?.active
                          ? "GreenCheckmarkIcon"
                          : "CloseIconCircled"
                      }
                      width="1rem"
                      height="1rem"
                    />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className={styles.currentItemContentFlex}>
            <div>
              <div className={styles.currentItemContentTitle}>Content</div>
              <div
                className={clsx(styles.currentItemContentSuggestion, {
                  ["rtl"]: isRtl(detail?.lang) && detail?.inner_text,
                })}
                lang={detail?.lang || undefined}
              >
                {detail?.inner_text || "Blank"}
              </div>
            </div>
            <div>
              <div className={styles.currentItemContentTitle}>Existing</div>
              <div
                className={clsx(styles.currentItemContentSuggestion, {
                  ["rtl"]: isRtl(detail?.lang) && detail?.tag,
                })}
                lang={detail?.lang || undefined}
              >
                {detail?.tag || "Blank"}
              </div>
            </div>
          </div>
        </div>
      );
    default:
      return (
        <div key={detail.id} className={styles.currentItemContent}>
          <div className={styles.currentItemContentHeader}>
            <div className={styles.currentItemContentTitle}>
              {detail?.description && detail?.description}
            </div>
            <div className={styles.currentItemContentHeaderIcons}>
              {categorySlug !== "too-many-h1s" && (
                <Button
                  disabled={isDisabled}
                  variant="nohoverWithPadding"
                  onClick={(event) => editSuggestion(detail, event)}
                >
                  <Icon iconName="EditIcon" width="1rem" height="1rem" />
                </Button>
              )}
              <div className={styles.currentItemContentHeaderIcon}>
                {isCurrentItemLoading ? (
                  <Spinner size="1rem" />
                ) : (
                  <Button
                    disabled={isDisabled}
                    variant="nohoverWithPadding"
                    onClick={(event) => handleApprove(detail, event)}
                  >
                    <Icon
                      iconName={
                        detail?.active
                          ? "GreenCheckmarkIcon"
                          : "CloseIconCircled"
                      }
                      width="1rem"
                      height="1rem"
                    />
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className={styles.currentItemContentFlex}>
            <div>
              <div className={styles.currentItemContentTitle}>Content</div>
              <div
                className={clsx(styles.currentItemContentSuggestion, {
                  ["rtl"]:
                    isRtl(detail?.lang) &&
                    (detail?.content || detail?.inner_text),
                })}
                lang={detail?.lang || undefined}
              >
                {detail?.content || detail?.inner_text || "Blank"}
              </div>
            </div>
            <div>
              <div className={styles.currentItemContentTitle}>Existing</div>
              <div
                className={clsx(styles.currentItemContentSuggestion, {
                  ["rtl"]:
                    isRtl(detail?.lang) && (detail?.original || detail?.tag),
                })}
                lang={detail?.lang || undefined}
              >
                {detail?.original || detail?.tag || "Blank"}
              </div>
            </div>
            <div>
              <div className={styles.currentItemContentTitle}>Suggestion</div>
              <div
                className={clsx(styles.currentItemContentSuggestion, {
                  ["rtl"]: isRtl(detail?.lang) && detail?.final,
                })}
                lang={detail?.lang || undefined}
              >
                {detail?.final || "Blank"}
              </div>
            </div>
          </div>
        </div>
      );
  }
};

const CategoryPageItem = ({
  isDefaultOpen,
  item,
  approve,
  isLoading,
  isDisabled = false,
  hasPendingStatus,
  currentCategory,
  editPageSuggestion,
  isWidgetActionLoading,
  fetchDetails,
}: CategoryPageItemProps) => {
  const [wrapperRef, wrapperWidth] = useElementWidth<HTMLDivElement>(300);
  const [isOpen, setIsOpen] = useState(isDefaultOpen);
  const [lastClickedId, setLastClickedId] = useState<number | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editedSuggestion, setEditedSuggestion] = useState<EditedSuggestion>({
    final: "",
    active: false,
    lang: null,
  });
  const [currentEditDetail, setCurrentEditDetail] =
    useState<PageRecommendation | null>(null);
  const [loadingPageId, setLoadingPageId] = useState<number | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const allItemsInPageAreApproved = item?.approved === item?.total;

  const editSuggestion = (
    detail: PageRecommendation,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    setCurrentEditDetail(detail);
    setEditedSuggestion({
      final:
        detail.final ||
        detail.suggestion ||
        detail.content ||
        detail.inner_text ||
        "",
      active: !!detail.active,
      lang: detail.lang,
    });
    setIsEditModalOpen(true);
  };

  const handleModalSubmit = () => {
    if (!currentEditDetail?.id) return;

    if (currentEditDetail?.id) {
      setLastClickedId(currentEditDetail?.id);
    }

    approve({
      kind: editedSuggestion.active ? "approve" : "unapprove",
      ids: [currentEditDetail.id],
      variant: "automation",
    });

    if (editedSuggestion.final !== currentEditDetail.final) {
      editPageSuggestion({
        id: currentEditDetail.id,
        final: editedSuggestion.final,
      });
    }
  };

  useEffect(() => {
    if (!isLoading && !isWidgetActionLoading) {
      setLastClickedId(null);
      setLoadingPageId(null);
      setIsEditModalOpen(false);
    }
  }, [isLoading, isWidgetActionLoading]);

  const toggleContent = async () => {
    if (!isOpen && item && (!item.details || item.details.length === 0)) {
      setIsLoadingDetails(true);
      try {
        await fetchDetails?.(item.id);
      } catch (error) {
        console.error(`Error fetching details for page ${item.id}:`, error);
      } finally {
        setIsLoadingDetails(false);
      }
    }
    setIsOpen(!isOpen);
  };

  const handleAllApprove = (item: PageItem, event: React.MouseEvent) => {
    event.stopPropagation();
    setLoadingPageId(item.id);

    approve({
      kind: allItemsInPageAreApproved ? "unapprove" : "approve",
      page_id: item?.id,
      variant: "automation",
      singleItemUpdate: true,
      category_id: currentCategory?.id,
    });
  };

  const handleApprove = (
    detail: PageRecommendation,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    if (detail?.id) {
      setLastClickedId(detail?.id);
    }

    approve({
      kind: detail.active ? "unapprove" : "approve",
      ids: detail.id ? [detail.id] : undefined,
      variant: "automation",
      page_id: detail.page_id,
      singleItemUpdate: true,
    });
  };

  return (
    <>
      <div className={styles.currentItem}>
        <div className={styles.currentItemHeader} onClick={toggleContent}>
          {isLoadingDetails ? (
            <Spinner size="1rem" />
          ) : (
            <Icon
              iconName={isOpen ? "IconDown" : "ArrowRightIcon"}
              width="1rem"
              height="1rem"
            />
          )}
          <div className={styles.currentItemHeaderTitle} ref={wrapperRef}>
            {item.path_and_query && (
              <MiddleTruncateTextPx
                text={item.path_and_query}
                maxWidth={wrapperWidth}
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              />
            )}
            <div className={styles.currentItemHeaderIcons}>
              {isLoading && loadingPageId === item.id ? (
                <Button variant="nohoverWithPadding">
                  <Spinner size="1rem" />
                </Button>
              ) : (
                <Button
                  variant="nohoverWithPadding"
                  onClick={(event) => handleAllApprove(item, event)}
                  disabled={isDisabled}
                >
                  <Icon
                    iconName={
                      allItemsInPageAreApproved
                        ? "GreenCheckmarkIcon"
                        : "CloseIconCircled"
                    }
                    width="1rem"
                    height="1rem"
                  />
                </Button>
              )}
            </div>
          </div>
        </div>

        {isOpen && (
          // @ts-expect-error TS2339 non-existing className
          <div className={styles.itemContent}>
            {item.details?.map((detail) => {
              const isCurrentItemLoading =
                (isLoading &&
                  (lastClickedId === detail.id || hasPendingStatus)) ||
                (isWidgetActionLoading && lastClickedId === detail.id);

              return renderContent(
                detail,
                isCurrentItemLoading,
                handleApprove,
                currentCategory?.slug,
                editSuggestion,
                isDisabled,
              );
            })}
          </div>
        )}
      </div>

      <Modal
        isLoading={isWidgetActionLoading || isLoading}
        isOpen={isEditModalOpen}
        closeModal={() => setIsEditModalOpen(false)}
        buttonLabel="Update"
        formId="editSuggestion"
        modalLabel={
          // @ts-expect-error TS2339 non-existing className
          <h3 className={styles.modalTitle}>
            Edit {currentEditDetail?.description || "Suggestion"}
          </h3>
        }
        action={handleModalSubmit}
      >
        <ContentSchemaModal
          editedSuggestion={editedSuggestion}
          setEditedSuggestion={setEditedSuggestion}
        />
      </Modal>
    </>
  );
};

export default CategoryPageItem;
